import { useContext, useEffect, useRef } from 'react';
import { Text } from '@fluentui/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSetAtom } from 'jotai';
import { EditItem } from '@moko/ui';
import { getApiErrorMessage } from '@moko/utils';
import AppContext from '../AppContext';
import {
  errorMessageAtom,
  successMessageAtom,
} from '../../atoms/messageBarAtoms';
import {
  createBillingClass,
  getBillingClass,
  updateBillingClass,
  type IBillingClassBody,
} from '../../services/settingsService';
import { billingClassesPath } from './pricingSettings';

const text = (value: unknown) =>
  typeof value === 'string' ? value.trim() : '';

const toBody = (item: Record<string, unknown>): IBillingClassBody | string => {
  const code = text(item.code);
  const name = text(item.name);
  if (!code || !name) return 'Code and name are required.';
  return { code, name, description: text(item.description) };
};

const BillingClassForm = () => {
  const isSaved = useRef(false);
  const navigate = useNavigate();
  const query = new URLSearchParams(useLocation().search);
  const billingClassId = Number.parseInt(query.get('id') ?? '');
  const isEdit = !Number.isNaN(billingClassId) && billingClassId > 0;
  const setSuccessMessage = useSetAtom(successMessageAtom);
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const context = useContext(AppContext);
  const title = isEdit ? 'Edit billing class' : 'Add billing class';

  useEffect(() => {
    context.setSelectedTab('Global settings');
  }, []);

  const save = async (
    abortController: AbortController,
    item: Record<string, unknown>,
    id?: number
  ) => {
    const body = toBody(item);
    if (typeof body === 'string') {
      setErrorMessage(body);
      throw new Error(body);
    }
    try {
      if (id == null) {
        await createBillingClass(abortController, body);
        setSuccessMessage(`Billing class ${body.code} created.`);
      } else {
        await updateBillingClass(abortController, id, body);
        setSuccessMessage(`Billing class ${body.code} updated.`);
      }
      navigate(billingClassesPath);
    } catch (error) {
      setErrorMessage(
        getApiErrorMessage(error, 'Failed to save billing class.')
      );
      throw error;
    }
  };

  return (
    <div style={{ width: '100%', maxWidth: 640 }}>
      <Text variant="xLarge">{title}</Text>
      <EditItem
        getAction={(abortController, id) =>
          getBillingClass(abortController, id)
        }
        newAction={(abortController, item) => save(abortController, item)}
        editAction={(abortController, id, item) =>
          save(abortController, item, id)
        }
        isSaved={isSaved}
        back={billingClassesPath}
        confirmOnCancel={false}
        onCancel={() => navigate(billingClassesPath)}
        metadata={{
          fields: [
            {
              name: 'code',
              fieldType: 'String',
              label: 'Code',
              mandatory: true,
            },
            {
              name: 'name',
              fieldType: 'String',
              label: 'Name',
              mandatory: true,
            },
            { name: 'description', fieldType: 'String', label: 'Description' },
          ],
          lookups: [],
          validations: [
            {
              field: 'code',
              rules: [{ rule: 'NE', errorMessage: 'Code is required.' }],
            },
            {
              field: 'name',
              rules: [{ rule: 'NE', errorMessage: 'Name is required.' }],
            },
          ],
          interactions: [],
        }}
      />
    </div>
  );
};

export default BillingClassForm;
